import type { CSSProperties } from "react";
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import styles from "./overview/Overview.module.scss";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import { useCarConfigSelector } from "../../../../../redux/store";
import ConfigCtas from "../../../common/cta/ConfigCtas";
import SummaryPanel from "./SummaryPanel";
import { useCarConfigCCISNormalization, useCarIds } from "../../../../../hooks/useAphCCISNormalization";
import {
    getColorVisual,
    getColorBackgroundVisual,
    getEngineVisual,
    getPacksVisual,
    getUpholsteryVisual,
    getWheelsVisual,
    getChargingVisual,
} from "../../../../../utils/visualUtils";
import { getCarImage } from "../../../../../../shared-logic/utils/imageUtils";
import SliderWrapper from "../../../common/spin/sliderWrapper/SliderWrapper";

const Summary: React.FC = () => {
    const car = useCarConfigSelector((state) => state.car);
    const carConfigMotorization = useCarConfigSelector((state) => state.carConfigMotorization);
    const wheel = useCarConfigSelector((state) => state.basket.wheel);
    const upholstery = useCarConfigSelector((state) => state.basket.upholstery);
    const homeChargers = useCarConfigSelector((state) => state.basket.extras.filter((extra) => extra.isCharger));
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const standardPacks = useCarConfigSelector((state) => state.items.packs.filter((pack) => pack.standard));
    const extras = useCarConfigSelector((state) => state.basket.extras.filter((extra) => !extra.isCharger));
    const colour = useCarConfigSelector((state) => state.basket.colour);
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);

    const hasPacksOrExtras = packs.length !== 0 || extras.length !== 0 || standardPacks.length !== 0;
    const hasHomeChargers = homeChargers.length !== 0;
    const sliderIsInitialized = useCarConfigSelector((state) => state.car.initialized);

    const primaryVisual = getCarImage(
        commonSettings,
        12,
        carIds,
        {
            type: "webp",
            width: 1000,
            height: 700,
        },
        carImageInput,
    );

    const ctaRef = useRef<HTMLDivElement | null>(null);
    const [overflowerHeight, setOverflowerHeight] = useState<string | number>("unset");

    useEffect(() => {
        if (!ctaRef.current) return undefined;
        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (ctaRef?.current?.clientHeight) setOverflowerHeight(ctaRef?.current?.clientHeight);
            });
        });
        resizeObserver.observe(ctaRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <>
            <Text tag="h2" variant="apheleia-h2" spacing="spacing-3">
                <CarConfigLabel label="carConfigOrderSummary" />
            </Text>
            <SliderWrapper sliderFrames={[13, 3, 0, 18]} />
            <div className={styles.mobilecta}>
                <ConfigCtas />
            </div>
            <div className={styles.overview} style={{ "--or-cta-height": overflowerHeight } as CSSProperties}>
                <div>
                    <SummaryPanel />
                </div>
                <div>
                    <div className={styles.imagegrid}>
                        {sliderIsInitialized && (
                            <div>
                                <img
                                    src={primaryVisual.src}
                                    srcSet={primaryVisual.srcSet}
                                    alt=""
                                    role="presentation"
                                    className={styles["imagegrid-visual-main"]}
                                />
                            </div>
                        )}
                        <div
                            style={{
                                "--or-animation-delay": 0.25,
                            }}
                            className={styles["imagegrid-visual"]}
                            aria-describedby="visual-description-engine"
                        >
                            <img
                                src={getEngineVisual(car, carConfigMotorization, commonSettings)}
                                alt={useCarConfigLabel("carConfigEngine")}
                            />
                            <div id="visual-description-engine" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigEngine" />
                            </div>
                        </div>
                        <div
                            className={styles["imagegrid-visual"]}
                            style={{
                                background: getColorBackgroundVisual(colour, commonSettings, carIds),
                                "--or-animation-delay": 0.35,
                            }}
                            aria-describedby="visual-description-colour"
                        >
                            <img
                                src={getColorVisual(colour, commonSettings, carIds, carImageInput)}
                                alt={useCarConfigLabel("carConfigColour")}
                            />
                            <div id="visual-description-colour" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigColour" />
                            </div>
                        </div>
                        <div
                            className={clsx(styles["imagegrid-visual"], styles["imagegrid-wheel"])}
                            style={{
                                "--or-animation-delay": 0.45,
                            }}
                            aria-describedby="visual-description-wheels"
                        >
                            <img
                                src={getWheelsVisual(wheel, commonSettings)}
                                alt={useCarConfigLabel("carConfigWheels")}
                            />
                            <div id="visual-description-wheels" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigWheels" />
                            </div>
                        </div>
                        <div
                            className={styles["imagegrid-visual"]}
                            style={{
                                "--or-animation-delay": 0.55,
                            }}
                            aria-describedby="visual-description-upholstery"
                        >
                            <img
                                src={getUpholsteryVisual(upholstery, commonSettings)}
                                alt={useCarConfigLabel("carConfigUpholstery")}
                            />
                            <div id="visual-description-upholstery" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigUpholstery" />
                            </div>
                        </div>
                        <div
                            style={{
                                "--or-animation-delay": 0.65,
                            }}
                            className={clsx(styles["imagegrid-visual"], !hasPacksOrExtras && styles.hide)}
                            aria-describedby="visual-description-packsorextras"
                        >
                            <img
                                src={getPacksVisual([...packs, ...standardPacks], extras, commonSettings)}
                                alt={useCarConfigLabel("carConfigPacksAndExtras")}
                            />
                            <div id="visual-description-packsorextras" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigPacksAndExtras" />
                            </div>
                        </div>
                        <div
                            style={{
                                "--or-animation-delay": 0.75,
                            }}
                            className={clsx(styles["imagegrid-visual"], !hasHomeChargers && styles.hide)}
                            aria-describedby="visual-description-chargingathome"
                        >
                            <img
                                src={getChargingVisual(homeChargers, commonSettings)}
                                alt={useCarConfigLabel("carConfigChargingAtHome")}
                            />
                            <div id="visual-description-chargingathome" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigChargingAtHome" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Summary;
